// A site encounter imported, and the DM's encounter presets (issue 1047; COMBAT-008, COMBAT-010), in the DM's turn
// tracker. "Import from the site…" opens a small dialog listing the DM's encounters saved on the site (built there:
// issue 1048, option B) with Hidden ticked — a surprise is the common case — and Import, which copies the chosen one
// onto the scene in view: its enemies as tokens and combatants on the free squares near the view's centre, its players
// skipped. The Presets section saves the current fight under a name and lists the table's presets with Load and Delete.
// The server does the placing; every refusal is said in its own words.
import { useCallback, useEffect, useState } from 'react';
import { EMPTY_GRID, boardExtent, type Point } from '@dragons-table/vtt-shared';
import { Modal } from '../Dialogs';
import { useShell } from '../context';
import { useTableState } from '../../table/TableState';
import { useBoardMap } from '../../board/sceneMap';
import { viewCentreOr } from '../../place';
import {
  IMPORT_MAX, deletePreset, freeSpots, importNotice, importSiteEncounter, loadPreset, presetList, savePreset, siteEncounterList,
  type PresetRow, type SiteEncounterRow,
} from '../../encounterImport';
import { track } from '../../analytics';
import { BTN, PRIMARY } from '../ui';

const btn = BTN;
const primary = PRIMARY;
const UNREACHABLE = "The table can't be reached just now. Try again in a moment.";

/** The free squares near the DM's view an import or a load lands on, read at the moment of asking. */
function useSpots(): () => Point[] {
  const { store } = useTableState();
  const map = useBoardMap();
  return () => {
    const objects = Object.values(store.state.objects);
    const centre = viewCentreOr({ x: EMPTY_GRID.squarePx * 2.5, y: EMPTY_GRID.squarePx * 2.5 });
    return freeSpots(store.state.grid, boardExtent(map), centre, store.state.scene.snap !== false, objects.map((o) => ({ x: o.x, y: o.y })), IMPORT_MAX);
  };
}

export function ImportDialog({ onClose }: { onClose: () => void }) {
  const { campaignId } = useShell();
  const { scene, setNotice } = useTableState();
  const spots = useSpots();
  const [list, setList] = useState<SiteEncounterRow[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [hidden, setHidden] = useState(true);
  const [said, setSaid] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    void siteEncounterList(campaignId).then((r) => {
      if (!live) return;
      if (r.kind === 'ok') { setList(r.encounters); setChosen(r.encounters[0]?.id ?? null); return; }
      setList([]);
      setSaid(r.kind === 'refused' ? r.reason : "The site can't be reached just now. Try again in a moment.");
    });
    return () => { live = false; };
  }, [campaignId]);
  const doImport = async () => {
    if (!chosen) return;
    setBusy(true); setSaid('');
    const r = await importSiteEncounter(campaignId, chosen, { hidden, spots: spots() }, scene?.id ?? null);
    setBusy(false);
    if (r.kind === 'ok') { track('token-place', { from: 'import' }); setNotice(importNotice(r.name, r.added, r.skipped)); onClose(); return; }
    setSaid(r.kind === 'refused' ? r.reason : UNREACHABLE);
  };
  return (
    <Modal title="Import from the site" onClose={onClose} testId="import-encounter">
      <form className="space-y-3 text-sm" onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); if (!busy) void doImport(); }}>
        <p className="text-xs text-stone-400">A copy of an encounter you saved in the site's initiative tracker: its creatures come as tokens in the turn order; its players are skipped (they are the party's own tokens).</p>
        {list === null && <p className="text-stone-400">Reading your encounters…</p>}
        {list !== null && list.length === 0 && !said && <p className="text-stone-400" data-testid="import-empty">You have no saved encounters on the site for this game system. Build one in the site's initiative tracker and save it.</p>}
        {list !== null && list.length > 0 && (
          <fieldset className="max-h-64 space-y-1 overflow-y-auto">
            <legend className="sr-only">Your encounters on the site</legend>
            {list.map((e) => (
              <label key={e.id} className="flex items-center gap-2 rounded px-1 py-0.5 hover:bg-stone-800">
                <input type="radio" name="site-encounter" checked={chosen === e.id} onChange={() => setChosen(e.id)} aria-label={`${e.name}, ${e.combatantCount} combatants`} />
                <span className="flex-1">{e.name}</span>
                <span className="text-xs text-stone-400">{e.combatantCount === 1 ? '1 combatant' : `${e.combatantCount} combatants`}</span>
              </label>
            ))}
          </fieldset>
        )}
        <label className="flex items-center gap-2"><input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} /> Hidden</label>
        {said && <p role="alert" className="text-amber-300">{said}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded border border-stone-600 px-3 py-1 text-sm hover:bg-stone-800" onClick={onClose}>Cancel</button>
          <button type="submit" className={primary} disabled={busy || !chosen}>Import</button>
        </div>
      </form>
    </Modal>
  );
}

export function PresetsSection() {
  const { campaignId } = useShell();
  const { scene, setNotice } = useTableState();
  const spots = useSpots();
  const [presets, setPresets] = useState<PresetRow[] | null>(null);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [hidden, setHidden] = useState(true);
  const [said, setSaid] = useState('');
  const [busy, setBusy] = useState(false);
  const reload = useCallback(async () => {
    const r = await presetList(campaignId);
    if (r.kind === 'ok') { setPresets(r.presets); return; }
    setPresets([]);
    setSaid(r.kind === 'refused' ? r.reason : UNREACHABLE);
  }, [campaignId]);
  useEffect(() => { void reload(); }, [reload]);
  const save = async () => {
    const n = name.trim();
    if (!n) { setSaid('Give the preset a name.'); return; }
    setBusy(true); setSaid('');
    const r = await savePreset(campaignId, n, scene?.id ?? null);
    setBusy(false);
    if (r.kind !== 'ok') { setSaid(r.kind === 'refused' ? r.reason : UNREACHABLE); return; }
    setNotice(`Saved '${r.preset.name}'${r.skipped ? ` (${r.skipped} left out: no token, or a player's)` : ''}.`);
    setNaming(false); setName('');
    await reload();
  };
  const load = async (p: PresetRow) => {
    setBusy(true); setSaid('');
    const r = await loadPreset(campaignId, p.id, { hidden, spots: spots() }, scene?.id ?? null);
    setBusy(false);
    if (r.kind === 'ok') { track('token-place', { from: 'preset' }); setNotice(importNotice(r.name, r.added, r.skipped)); return; }
    setSaid(r.kind === 'refused' ? r.reason : UNREACHABLE);
  };
  const remove = async (p: PresetRow) => {
    setSaid('');
    if (!(await deletePreset(campaignId, p.id))) setSaid(`'${p.name}' could not be deleted. Try again in a moment.`);
    await reload();
  };
  return (
    <section className="space-y-1 border-t border-stone-800 pt-2" data-testid="presets" aria-label="Presets">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="vtt-section-head">Presets</h3>
        <label className="flex items-center gap-1 text-[0.6875rem]" title="Load a preset with every creature hidden; unticked, each stands as it was saved."><input type="checkbox" aria-label="Load hidden" checked={hidden} onChange={(e) => setHidden(e.target.checked)} /> load hidden</label>
        {!naming && <button type="button" className={`${btn} ml-auto`} onClick={() => { setNaming(true); setSaid(''); }}>Save the current fight as a preset…</button>}
      </div>
      {naming && (
        <form className="flex flex-wrap items-center gap-1" onSubmit={(e) => { e.preventDefault(); if (!busy) void save(); }}>
          <input aria-label="Preset name" className="w-40 rounded bg-stone-800 px-1 py-0.5 text-xs" placeholder="Bridge trolls" value={name} maxLength={80} autoFocus onChange={(e) => { setName(e.target.value); setSaid(''); }} />
          <button type="submit" className={btn} disabled={busy}>Save</button>
          <button type="button" className={btn} onClick={() => { setNaming(false); setName(''); setSaid(''); }}>Cancel</button>
        </form>
      )}
      {said && <p role="alert" className="text-xs text-amber-300">{said}</p>}
      {presets !== null && presets.length === 0 && <p className="text-[0.6875rem] text-stone-500">No presets yet. Save a fight to bring it back later.</p>}
      {presets !== null && presets.length > 0 && (
        <ul className="divide-y divide-stone-800">
          {presets.map((p) => (
            <li key={p.id} className="flex items-center gap-2 py-1 text-xs">
              <span className="flex-1">{p.name}</span>
              <span className="text-stone-400" title="Creatures">{p.count}</span>
              <button type="button" className={btn} disabled={busy} aria-label={`Load ${p.name}`} onClick={() => void load(p)}>Load</button>
              <button type="button" className={btn} aria-label={`Delete ${p.name}`} onClick={() => void remove(p)}>Delete</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The DM's import button and its dialog. */
export function ImportButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={btn} title="Copy an encounter you built on the site into this fight" onClick={() => setOpen(true)}>Import from the site…</button>
      {open && <ImportDialog onClose={() => setOpen(false)} />}
    </>
  );
}
